/**
 * RaceList — displays race entries in the editor panel.
 *
 * Shows race cards grouped by year with edit, delete, and highlight controls.
 * Communicates user actions via callbacks.
 */
export class RaceList {
  /**
   * @param {Object} opts
   * @param {Function} opts.onEdit   - called with race object when edit is clicked
   * @param {Function} opts.onDelete - called with race id when delete is clicked
   * @param {Function} opts.onHighlightToggle - called with race id when highlight toggled
   */
  constructor({ onEdit, onDelete, onHighlightToggle }) {
    this._onEdit = onEdit;
    this._onDelete = onDelete;
    this._onHighlightToggle = onHighlightToggle;
    this._container = null;
    this._listEl = null;
    this._addBtn = null;
    this._races = [];
    this._query = "";
    this._filter = "all";
    this._closedYears = new Set();
    this._onAddCallbacks = [];
  }

  /* ------------------------------------------------------------------ */
  /*  Public API                                                         */
  /* ------------------------------------------------------------------ */

  /** Render the race list section into the given container. */
  render(container) {
    this._container = container;

    const section = document.createElement('div');
    section.className = 'race-list-section';

    // Header row
    const header = document.createElement('div');
    header.className = 'race-list-header';

    const label = document.createElement('div');
    label.className = 'editor-section-label';
    label.style.marginBottom = '0';
    label.textContent = 'Race History';

    const addBtn = document.createElement('button');
    addBtn.type = 'button';
    addBtn.className = 'race-list-add-btn';
    addBtn.textContent = '+ ADD RACE';
    this._addBtn = addBtn;

    addBtn.addEventListener('click', () => {
      this._onAddCallbacks.forEach((cb) => cb());
    });

    header.appendChild(label);
    header.appendChild(addBtn);
    section.appendChild(header);

    const search = document.createElement('input');
    search.className = 'editor-input';
    search.type = 'search';
    search.placeholder = 'Search races, places or years';
    search.setAttribute('aria-label', 'Search race history');
    search.addEventListener('input', () => { this._query = search.value.toLowerCase().trim(); this._renderList(); });
    const filter = document.createElement('select');
    filter.className = 'editor-input';
    filter.setAttribute('aria-label', 'Filter race history');
    for (const [value, label] of [['all','All races'],['road','Road'],['trail','Trail'],['highlight','Highlights'],['attention','Needs a location or time']]) {
      filter.add(new Option(label, value));
    }
    filter.addEventListener('change', () => { this._filter = filter.value; this._renderList(); });
    this._count = document.createElement('p');
    this._count.setAttribute('aria-live', 'polite');
    section.append(search, filter, this._count);

    // List container
    const listEl = document.createElement('div');
    listEl.className = 'race-list-items';
    this._listEl = listEl;
    section.appendChild(listEl);

    container.appendChild(section);

    this._renderList();
  }

  /** Update the displayed race list. */
  setRaces(races) {
    this._races = races || [];
    this._renderList();
  }

  /** Register a callback for the "+ ADD RACE" button. */
  addOnAddClick(callback) {
    this._onAddCallbacks.push(callback);
  }

  /* ------------------------------------------------------------------ */
  /*  Internal rendering                                                 */
  /* ------------------------------------------------------------------ */

  _renderList() {
    if (!this._listEl) return;
    this._listEl.innerHTML = '';

    if (this._races.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'race-list-empty';
      empty.textContent = 'No races added yet';
      this._listEl.appendChild(empty);
      return;
    }

    // Group races by year
    const visible = this._races.filter(r =>
      [r.raceName, r.location, r.date].join(' ').toLowerCase().includes(this._query) &&
      (this._filter === 'all' || r.type === this._filter ||
       this._filter === 'highlight' && r.isHighlighted ||
       this._filter === 'attention' && (!r.location || r.isTimed !== false && !r.time)));
    this._count.textContent = `${visible.length} of ${this._races.length} races`;
    const groups = this._groupByYear(visible);
    const years = Object.keys(groups).sort((a, b) => {
      // "Unknown" goes last, otherwise descending year
      if (a === 'Unknown') return 1;
      if (b === 'Unknown') return -1;
      return Number(b) - Number(a);
    });

    for (const year of years) {
      const group = document.createElement('details');
      group.open = !this._closedYears.has(year);
      const heading = document.createElement('summary');
      heading.className = 'race-year-group';
      heading.textContent = `${year} · ${groups[year].length} races`;
      group.append(heading);
      group.addEventListener('toggle', () => group.open ? this._closedYears.delete(year) : this._closedYears.add(year));
      for (const race of groups[year]) group.append(this._buildRaceCard(race));
      this._listEl.append(group);
    }
  }

  _groupByYear(races) {
    const groups = {};
    for (const race of races) {
      let year = 'Unknown';
      if (race.date) {
        const parsed = String(race.date).substring(0, 4);
        if (/^\d{4}$/.test(parsed)) {
          year = parsed;
        }
      }
      if (!groups[year]) groups[year] = [];
      groups[year].push(race);
    }
    return groups;
  }

  _buildRaceCard(race) {
    const card = document.createElement('div');
    card.className = 'race-card';

    // Body (name + details)
    const body = document.createElement('div');
    body.className = 'race-card-body';

    const name = document.createElement('div');
    name.className = 'race-card-name';
    name.textContent = race.raceName;

    const details = document.createElement('div');
    details.className = 'race-card-details';
    const parts = [];
    if (race.distance != null) parts.push(`${race.distance}km`);
    if (race.time) parts.push(race.time);
    if (race.type === 'trail' && race.elevation != null) parts.push(`${race.elevation}m elev`);
    details.textContent = parts.join(' \u2022 ');

    body.appendChild(name);
    body.appendChild(details);
    card.appendChild(body);

    // Highlight indicator
    const highlight = document.createElement('div');
    highlight.className = 'race-card-highlight';
    highlight.textContent = race.isHighlighted ? '\u2605' : '';
    highlight.setAttribute('role', 'button');
    highlight.tabIndex = 0;
    highlight.setAttribute('aria-label', `Toggle highlight for ${race.raceName}`);
    highlight.addEventListener('keydown', event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); this._onHighlightToggle?.(race.id); } });
    highlight.title = race.isHighlighted ? 'Highlighted' : '';
    highlight.style.cursor = 'pointer';
    highlight.addEventListener('click', () => {
      this._onHighlightToggle?.(race.id);
    });
    card.appendChild(highlight);

    // Actions
    const actions = document.createElement('div');
    actions.className = 'race-card-actions';

    // Edit button
    const editBtn = document.createElement('button');
    editBtn.type = 'button';
    editBtn.className = 'race-card-action-btn race-card-action-btn--edit';
    editBtn.title = 'Edit';
    editBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>`;
    editBtn.addEventListener('click', () => this._onEdit?.(race));
    actions.appendChild(editBtn);

    // Delete button
    const deleteBtn = document.createElement('button');
    deleteBtn.type = 'button';
    deleteBtn.className = 'race-card-action-btn race-card-action-btn--delete';
    deleteBtn.title = 'Delete';
    deleteBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
    </svg>`;
    deleteBtn.addEventListener('click', () => this._onDelete?.(race.id));
    actions.appendChild(deleteBtn);

    card.appendChild(actions);

    return card;
  }
}
